/**
 * V163: Cloud Function 기반 근로일정 조회 Hook (세션 캐시 도입)
 *
 * **V163 개선:**
 * - 모듈 스코프 세션 캐시 도입 (페이지 간 데이터 공유)
 * - 캐시 히트 시 즉시 데이터 반환 + 백그라운드 refetch
 * - TTL 60초 (stale 판정 기준)
 *
 * **핵심 원칙:**
 * - 근로일정 데이터는 Cloud Function(getMyWorkSchedule)이 유일한 소스
 * - Firestore 직접 조회 금지 → 대시보드/근로일정 페이지 일관성 보장
 *
 * **반환값:**
 * - workSchedule: 승인된 근로일정 (null이면 없음)
 * - pendingRequests: 승인대기 중인 요청 목록
 * - hasApproved: 승인된 근로일정 존재 여부 (출근 가능 판정용)
 * - isLoading: 로딩 중 여부
 * - isError: 에러 발생 여부
 * - refetch: 수동 재조회 함수
 *
 * **사용처:**
 * - WorkMyDashboardPage: 출근 가능 판정
 * - WorkSchedulePage: 캘린더 색상 결정
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { useCurrentUser } from './useCurrentUser';
import { WorkSchedule } from '../types/workSchedule';
import { hasApprovedWorkSchedule as checkHasApproved } from '../utils/workScheduleUtils';

/**
 * 승인요청 타입 (CF 응답용)
 */
interface PendingRequest {
  id: string;
  type: 'workSchedule' | 'workScheduleChange';
  status: 'pending';
  requestData: {
    month?: string;
    date?: string;
    base?: any;
    changes?: any;
    dailyOverrides?: Record<string, any>; // V280.1: 템플릿 dailyOverrides
  };
  [key: string]: any;
}

/**
 * V5.16: 월간 근무 통계 (Backend SSoT)
 */
export interface MonthlyWorkStats {
  /** 근무일수 (공휴일 제외) */
  workingDays: number;
  /** 소정근로시간 (분) */
  regularMinutes: number;
  /** 소정근로시간 (시간) */
  regularHours: number;
  /** 제외된 공휴일 목록 */
  excludedHolidays: Array<{ date: string; name: string }>;
}

/**
 * V55.47: 승인된 휴가 이벤트 (캘린더 표시용)
 */
export interface ApprovedLeaveEvent {
  id: string;                   // approvalRequest 문서 ID
  date: string;                 // YYYY-MM-DD
  leaveTypeCode: string;        // annual, halfDayMorning, halfDayAfternoon, etc.
  leaveTypeName: string;        // 연차휴가, 오전반차, 오후반차, etc.
  unit: 'fullDay' | 'morningHalf' | 'afternoonHalf' | 'hourly';
  /** V64: hourly 휴가의 시작/종료 시각 (HH:mm) + 시간 수 */
  startTime?: string;
  endTime?: string;
  hours?: number;
}

/**
 * V66: 휴직(LoA) 이벤트 (캘린더 표시용)
 *
 * SSoT: leaveOfAbsences 컬렉션 (휴가와 별도 도메인)
 */
export interface LeaveOfAbsenceEvent {
  id: string;
  startDate: string;            // YYYY-MM-DD
  endDate: string;              // YYYY-MM-DD
  loaType: 'parental' | 'familyCare' | 'general' | 'medical';
  loaTypeName: string;
  status: 'approved' | 'inProgress' | 'returned' | 'cancelled';
}

/**
 * V55.47: 승인된 연장근무 이벤트 (캘린더 표시용)
 */
export interface ApprovedOvertimeEvent {
  id: string;                   // approvalRequest 문서 ID
  date: string;                 // YYYY-MM-DD
  type: 'overtime' | 'weekendWork' | 'holidayWork';
  startTime: string;            // HH:mm
  endTime: string;              // HH:mm
  endDateOffset: 0 | 1;         // 0=당일종료, 1=익일종료
}

/**
 * Cloud Function 응답 타입
 */
interface GetMyWorkScheduleResponse {
  workSchedule: WorkSchedule | null;
  pendingRequests: PendingRequest[];
  monthlyWorkStats: MonthlyWorkStats | null; // V5.16
  approvedLeaves: ApprovedLeaveEvent[];       // V55.47
  approvedOvertimes: ApprovedOvertimeEvent[]; // V55.47
  approvedLeavesOfAbsence?: LeaveOfAbsenceEvent[]; // V66
}

/**
 * Hook 반환 타입
 */
export interface UseApprovedWorkScheduleResult {
  /** 승인된 근로일정 (null이면 없음) */
  workSchedule: WorkSchedule | null;
  /** 승인대기 중인 요청 목록 */
  pendingRequests: PendingRequest[];
  /** V5.16: 월간 근무 통계 (Backend SSoT) */
  monthlyWorkStats: MonthlyWorkStats | null;
  /** V55.47: 승인된 휴가 이벤트 목록 */
  approvedLeaves: ApprovedLeaveEvent[];
  /** V55.47: 승인된 연장근무 이벤트 목록 */
  approvedOvertimes: ApprovedOvertimeEvent[];
  /** V66: 휴직(LoA) 이벤트 목록 */
  approvedLeavesOfAbsence: LeaveOfAbsenceEvent[];
  /** 승인된 근로일정 존재 여부 (출근 가능 판정용) */
  hasApproved: boolean;
  /** 로딩 중 여부 */
  isLoading: boolean;
  /** 에러 발생 여부 */
  isError: boolean;
  /** 에러 메시지 */
  errorMessage: string | null;
  /** 수동 재조회 함수 */
  refetch: () => Promise<void>;
}

// ============================================
// V163: 세션 메모리 캐시 (모듈 스코프)
// ============================================

interface CacheEntry {
  data: {
    workSchedule: WorkSchedule | null;
    pendingRequests: PendingRequest[];
    monthlyWorkStats: MonthlyWorkStats | null; // V5.16
    approvedLeaves: ApprovedLeaveEvent[];       // V55.47
    approvedOvertimes: ApprovedOvertimeEvent[]; // V55.47
    approvedLeavesOfAbsence: LeaveOfAbsenceEvent[]; // V66
  };
  fetchedAt: number;
}

/**
 * V163: 캐시 저장소
 * key = `${userId}_${month}` (예: "emp123_2025-01")
 */
const sessionCache = new Map<string, CacheEntry>();

/**
 * V163: 캐시 TTL (60초)
 * - 60초 이내: 캐시에서 즉시 반환 + 백그라운드 refetch
 * - 60초 초과: 캐시 무효, 새로 fetch
 */
const CACHE_TTL_MS = 60 * 1000;

/**
 * V163: 캐시 키 생성
 */
function getCacheKey(userId: string, month: string): string {
  return `${userId}_${month}`;
}

/**
 * V163: 캐시에서 데이터 가져오기
 * @returns 캐시 데이터 (TTL 만료 시 null)
 */
function getFromCache(userId: string, month: string): CacheEntry | null {
  const key = getCacheKey(userId, month);
  const entry = sessionCache.get(key);

  if (!entry) return null;

  const now = Date.now();
  const age = now - entry.fetchedAt;

  // TTL 만료 시 캐시 삭제
  if (age > CACHE_TTL_MS) {
    sessionCache.delete(key);
    return null;
  }

  return entry;
}

/**
 * V163: 캐시에 데이터 저장
 */
function setToCache(userId: string, month: string, data: CacheEntry['data']): void {
  const key = getCacheKey(userId, month);
  sessionCache.set(key, {
    data,
    fetchedAt: Date.now(),
  });
}

/**
 * V163: 캐시 무효화 (refetch 시 사용)
 */
export function invalidateWorkScheduleCache(userId?: string, month?: string): void {
  if (userId && month) {
    const key = getCacheKey(userId, month);
    sessionCache.delete(key);
  } else {
    sessionCache.clear();
  }
}

// ============================================
// V163: Hook 구현
// ============================================

/**
 * V163: Cloud Function 기반 근로일정 조회 Hook (세션 캐시 적용)
 *
 * @param month - 대상 월 "YYYY-MM" 형식
 * @returns UseApprovedWorkScheduleResult
 *
 * @example
 * ```tsx
 * const { workSchedule, hasApproved, isLoading } = useApprovedWorkSchedule('2025-01');
 *
 * if (isLoading) return <Loading />;
 * if (!hasApproved) return <NoScheduleAlert />;
 * ```
 */
export function useApprovedWorkSchedule(month: string): UseApprovedWorkScheduleResult {
  const { user } = useCurrentUser();
  const userId = user?.profile?.id || '';

  const [workSchedule, setWorkSchedule] = useState<WorkSchedule | null>(null);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [monthlyWorkStats, setMonthlyWorkStats] = useState<MonthlyWorkStats | null>(null); // V5.16
  const [approvedLeaves, setApprovedLeaves] = useState<ApprovedLeaveEvent[]>([]); // V55.47
  const [approvedOvertimes, setApprovedOvertimes] = useState<ApprovedOvertimeEvent[]>([]); // V55.47
  const [approvedLeavesOfAbsence, setApprovedLeavesOfAbsence] = useState<LeaveOfAbsenceEvent[]>([]); // V66
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 중복 호출 방지를 위한 ref
  const fetchingRef = useRef(false);
  const lastMonthRef = useRef<string | null>(null);
  const lastUserIdRef = useRef<string | null>(null);

  // V163: 실제 CF 호출 함수
  const fetchFromServer = useCallback(async (forceUpdate: boolean = true): Promise<CacheEntry['data'] | null> => {
    if (!userId || !month) {
      return null;
    }

    try {
      const functions = getFunctions();
      const getMyWorkScheduleFn = httpsCallable<{ month: string }, GetMyWorkScheduleResponse>(
        functions,
        'getMyWorkSchedule'
      );

      const result = await getMyWorkScheduleFn({ month });
      const data = {
        workSchedule: result.data.workSchedule || null,
        pendingRequests: result.data.pendingRequests || [],
        monthlyWorkStats: result.data.monthlyWorkStats || null, // V5.16
        approvedLeaves: result.data.approvedLeaves || [],       // V55.47
        approvedOvertimes: result.data.approvedOvertimes || [], // V55.47
        approvedLeavesOfAbsence: result.data.approvedLeavesOfAbsence || [], // V66
      };

      // 캐시 업데이트
      setToCache(userId, month, data);

      // 상태 업데이트
      if (forceUpdate) {
        setWorkSchedule(data.workSchedule);
        setPendingRequests(data.pendingRequests);
        setMonthlyWorkStats(data.monthlyWorkStats); // V5.16
        setApprovedLeaves(data.approvedLeaves);     // V55.47
        setApprovedOvertimes(data.approvedOvertimes); // V55.47
        setApprovedLeavesOfAbsence(data.approvedLeavesOfAbsence); // V66
        setIsError(false);
        setErrorMessage(null);
      }

      return data;
    } catch (err: any) {
      console.error('[V163] CF 조회 실패:', err);
      if (forceUpdate) {
        setIsError(true);
        setErrorMessage(err.message || '근로일정을 불러오는데 실패했습니다.');
      }
      return null;
    }
  }, [userId, month]);

  // V163: 데이터 로드 (캐시 우선 + 백그라운드 refetch)
  const fetchData = useCallback(async () => {
    if (!userId || !month) {
      setIsLoading(false);
      return;
    }

    // 중복 호출 방지 (동일 userId + month)
    if (fetchingRef.current && lastMonthRef.current === month && lastUserIdRef.current === userId) {
      return;
    }

    fetchingRef.current = true;
    lastMonthRef.current = month;
    lastUserIdRef.current = userId;

    // V163: 캐시 확인
    const cached = getFromCache(userId, month);

    if (cached) {
      // 캐시 히트: 즉시 데이터 반환
      setWorkSchedule(cached.data.workSchedule);
      setPendingRequests(cached.data.pendingRequests);
      setMonthlyWorkStats(cached.data.monthlyWorkStats); // V5.16
      setApprovedLeaves(cached.data.approvedLeaves || []); // V55.47
      setApprovedOvertimes(cached.data.approvedOvertimes || []); // V55.47
      setApprovedLeavesOfAbsence(cached.data.approvedLeavesOfAbsence || []); // V66
      setIsLoading(false);
      setIsError(false);
      setErrorMessage(null);

      // 백그라운드 refetch (stale-while-revalidate)
      fetchFromServer(true).finally(() => {
        fetchingRef.current = false;
      });
    } else {
      // 캐시 미스: 로딩 표시 후 fetch
      setIsLoading(true);
      setIsError(false);
      setErrorMessage(null);

      const data = await fetchFromServer(true);

      if (data) {
        setWorkSchedule(data.workSchedule);
        setPendingRequests(data.pendingRequests);
        setMonthlyWorkStats(data.monthlyWorkStats); // V5.16
        setApprovedLeaves(data.approvedLeaves);     // V55.47
        setApprovedOvertimes(data.approvedOvertimes); // V55.47
        setApprovedLeavesOfAbsence(data.approvedLeavesOfAbsence); // V66
      }

      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [userId, month, fetchFromServer]);

  // V163: 수동 refetch (캐시 무효화 후 재조회)
  const refetch = useCallback(async () => {
    if (userId && month) {
      invalidateWorkScheduleCache(userId, month);
    }
    fetchingRef.current = false; // 중복 호출 방지 해제
    await fetchData();
  }, [userId, month, fetchData]);

  // 월/유저 변경 시 자동 조회
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 승인 여부 계산 (통일 함수 사용)
  const hasApproved = checkHasApproved(workSchedule);

  return {
    workSchedule,
    pendingRequests,
    monthlyWorkStats, // V5.16: Backend SSoT
    approvedLeaves,   // V55.47
    approvedOvertimes, // V55.47
    approvedLeavesOfAbsence, // V66
    hasApproved,
    isLoading,
    isError,
    errorMessage,
    refetch,
  };
}
